import { useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import {
  Avatar,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import LocalOfferIcon from '@mui/icons-material/LocalOfferOutlined';

import productsApi from '../../../api/productsApi';
import { getErrorMessage } from '../../../utils/errorHandler';
import BrandFormDialog from '../../../sections/admin/products/BrandFormDialog';

export default function BrandsPage() {
  const { enqueueSnackbar } = useSnackbar();

  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [brandToDelete, setBrandToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchBrands = async () => {
    setLoading(true);
    try {
      const { data } = await productsApi.getBrands();
      setBrands(data.data);
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'No se pudo cargar la lista de marcas.'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBrands();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNew = () => {
    setEditingBrand(null);
    setFormOpen(true);
  };

  const handleEdit = (brand) => {
    setEditingBrand(brand);
    setFormOpen(true);
  };

  const handleFormSaved = () => {
    setFormOpen(false);
    fetchBrands();
  };

  const handleConfirmDelete = async () => {
    if (!brandToDelete) {
      return;
    }
    setDeleting(true);
    try {
      await productsApi.deleteBrand(brandToDelete.id);
      enqueueSnackbar('Marca eliminada correctamente.', { variant: 'success' });
      setBrandToDelete(null);
      fetchBrands();
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'No se pudo eliminar la marca.'), { variant: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        field: 'logo',
        headerName: '',
        width: 64,
        sortable: false,
        renderCell: (params) => (
          <Avatar src={params.value || undefined} variant="rounded" sx={{ width: 32, height: 32 }}>
            <LocalOfferIcon fontSize="small" />
          </Avatar>
        ),
      },
      { field: 'name', headerName: 'Nombre', flex: 1, minWidth: 220 },
      { field: 'slug', headerName: 'Slug', flex: 1, minWidth: 180 },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Acciones',
        width: 100,
        getActions: (params) => [
          <GridActionsCellItem
            key="edit"
            icon={<EditIcon />}
            label="Editar"
            onClick={() => handleEdit(params.row)}
          />,
          <GridActionsCellItem
            key="delete"
            icon={<DeleteIcon />}
            label="Eliminar"
            onClick={() => setBrandToDelete(params.row)}
          />,
        ],
      },
    ],
    []
  );

  return (
    <Stack spacing={3}>
      <Stack sx={{ alignItems: 'flex-start', justifyContent: 'space-between' }} direction="row">
        <div>
          <Typography variant="h4">Marcas</Typography>
          <Typography variant="body2" color="text.secondary">
            Marcas de fabricantes usadas para clasificar los productos del catálogo.
          </Typography>
        </div>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleNew}>
          Nueva marca
        </Button>
      </Stack>

      <Card>
        <DataGrid autoHeight rows={brands} columns={columns} loading={loading} disableRowSelectionOnClick />
      </Card>

      <BrandFormDialog open={formOpen} onClose={() => setFormOpen(false)} onSaved={handleFormSaved} brand={editingBrand} />

      <Dialog open={Boolean(brandToDelete)} onClose={() => setBrandToDelete(null)}>
        <DialogTitle>Eliminar marca</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Seguro que deseas eliminar <strong>{brandToDelete?.name}</strong>? No podrás hacerlo si tiene productos
            asociados.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBrandToDelete(null)} disabled={deleting}>
            Cancelar
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
